import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';
import {
  parseTaskeoUserRoleValue123,
  pickFirstCompanyRoleValueFromRecords,
} from '../utils/taskeo-company-role-response.util';
import {
  normalizeCompanyRoleFromClaim,
  normalizeCompanyMembershipRole,
  pickStrongestCompanyRole,
} from '../../features/company-context/utils/company-membership-role.util';
import { AuthService } from './auth.service';

type JsonRecord = Record<string, unknown>;

function asRecord(value: unknown): JsonRecord {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as JsonRecord) : {};
}

/**
 * Sincroniza `UserSession.role` com GET `/api/auth/me`, `/users/me`, etc. (`Role` + `RoleValue` / `roleValue`).
 * Sem rebaixar face a membership/JWT (`pickStrongestCompanyRole`).
 */
@Injectable({ providedIn: 'root' })
export class SessionRoleSyncService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);

  async syncSessionRoleFromMeEndpoints(): Promise<void> {
    const user = this.auth.currentUser();
    if (!user?.companyId?.trim()) return;

    const raw = await this.tryGetMePayload();
    if (!raw || !this.auth.isAuthenticated() || this.auth.currentUser()?.id !== user.id) return;

    const meRoleRaw = this.pickMeRoleRaw(raw);
    if (meRoleRaw === undefined || meRoleRaw === null) return;

    const cur = normalizeCompanyMembershipRole(user.role, 'Member');
    const fromMe = normalizeCompanyRoleFromClaim(meRoleRaw, cur);
    const next = pickStrongestCompanyRole(cur, fromMe);
    const roleValue =
      parseTaskeoUserRoleValue123(meRoleRaw) ??
      (next === 'Owner' ? 1 : next === 'Admin' ? 2 : next === 'Member' ? 3 : user.roleValue);

    if (next === cur && roleValue === user.roleValue) return;

    this.auth.setCurrentUser({ ...user, role: next, roleValue });
  }

  private async tryGetMePayload(): Promise<unknown | null> {
    const urls = ['/api/auth/me', '/api/me', '/users/me', '/me'];
    for (const url of urls) {
      if (!this.auth.isAuthenticated()) return null;
      try {
        return await firstValueFrom(this.http.get<unknown>(url).pipe(timeout(8_000)));
      } catch (error) {
        if (!(error instanceof HttpErrorResponse) || error.status !== 404) return null;
      }
    }
    return null;
  }

  private pickMeRoleRaw(raw: unknown): unknown {
    const root = asRecord(raw);
    const data = asRecord(root['data'] ?? root['Data'] ?? root['result'] ?? root['Result'] ?? root);
    const user = asRecord(data['user'] ?? data['User']);
    const company = asRecord(data['company'] ?? data['Company']);
    const membership = asRecord(data['membership'] ?? data['Membership']);

    const sources: JsonRecord[] = [root, data, user, company, membership];
    const fromValue = pickFirstCompanyRoleValueFromRecords(sources);
    if (fromValue !== undefined) return fromValue;

    const keys = [
      'role',
      'Role',
      'companyRole',
      'CompanyRole',
      'membershipRole',
      'MembershipRole',
      'userRole',
      'UserRole',
      'tenantRole',
      'TenantRole',
    ];

    for (const s of sources) {
      if (!s || !Object.keys(s).length) continue;
      for (const k of keys) {
        const v = s[k];
        if (v !== undefined && v !== null) return v;
      }
    }
    return undefined;
  }
}
